import { Injectable, NgZone, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { GOOGLE_SSO_CONFIG, isDebugMode } from '../../config/sso.config';
import { LoggerService } from './logger.service';
import { GoogleAccountsId, GoogleCredentialCallbackDetail } from '../models/window-extensions';

/**
 * Google Authentication Service
 *
 * Dedicated service for Google Sign-In using Google Identity Services (GIS).
 * Mirrors the structure of microsoft-auth.service.ts: deferred SDK
 * initialization, signal-based state, observables for consumers, and a
 * single entry point that hands the acquired credential to AuthService for
 * the backend exchange.
 *
 * Flow (design.md Decision 4 — ID-token verification, not auth-code exchange):
 * 1. `initialize()` loads the GIS SDK and calls `google.accounts.id.initialize`
 *    with a `credential` callback.
 * 2. `renderButton(parent)` renders the official Google sign-in button into a
 *    host element (the login screen provides the container).
 * 3. On user consent, GIS invokes the callback with a signed ID token
 *    (`credential`).
 * 4. The credential is surfaced via `credential$` / `handleCredential()`; the
 *    login component (or AuthService) POSTs it to
 *    `/parent-auth/sso/google`, where the backend verifies it server-side
 *    (GoogleIDToken.cfc) and sets the session cookie.
 *
 * The ID token is held in memory only — never persisted to
 * localStorage/sessionStorage (XSS blast-radius requirement, task 4.1 QA).
 *
 * Prerequisites:
 * 1. The GIS script tag in `src/index.html` (accounts.google.com/gsi/client).
 * 2. A real Google OAuth client ID in `environment.shared.ts` — the service
 *    fails LOUDLY at initialization when the placeholder is still set.
 * 3. The backend origin registered as an authorized JavaScript origin in the
 *    Google Cloud Console OAuth client.
 *
 * @injectable
 * @providedIn 'root'
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleAuthService {
  private readonly ngZone = inject(NgZone);
  private readonly logger = inject(LoggerService);

  private readonly destroy$ = new Subject<void>();
  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _credential = signal<string | null>(null);
  private _isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  /** Observable stream of Google credential-acquisition state */
  readonly isAuthenticated$ = toObservable(this._isAuthenticated);

  /** Observable stream of the latest acquired Google ID token (credential) */
  readonly credential$ = toObservable(this._credential);

  constructor() {
    // Don't automatically initialize - wait for first method call.
    // This prevents the GIS SDK from being accessed before it's ready.
    if (isDebugMode()) {
      this.logger.debug('Google Auth Service created - initialization deferred');
    }
  }

  /**
   * Ensure the GIS SDK is loaded and initialized
   *
   * @throws Error when the Google client ID is still the placeholder —
   *         a misconfigured Google SSO must fail loudly and visibly,
   *         never as a silently dead button (task 4.2).
   */
  async initialize(): Promise<void> {
    if (this._isInitialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeGis();
    await this.initializationPromise;
  }

  /**
   * Initialize the Google Identity Services SDK
   *
   * @private
   */
  private async initializeGis(): Promise<void> {
    const clientId = GOOGLE_SSO_CONFIG.clientId;

    // Fail loudly on the placeholder — do not render a button that can
    // never work (task 4.2: "never a silently dead button").
    if (!clientId || clientId === 'YOUR_GOOGLE_CLIENT_ID' || clientId.startsWith('qa-')) {
      this.logger.error(
        'Google Auth: GOOGLE client ID is not configured. ' +
          'Set the real Google OAuth client ID in src/environments/environment.shared.ts ' +
          '(SHARED_SSO.google.clientId) before enabling Google SSO.',
      );
      throw new Error(
        'Google SSO is not configured: missing Google OAuth client ID ' +
          '(SHARED_SSO.google.clientId in environment.shared.ts).',
      );
    }

    const gis = await this.waitForGisScript();
    if (!gis) {
      this.logger.error('Google Auth: GIS script (accounts.google.com/gsi/client) not loaded');
      throw new Error('Google Identity Services script failed to load');
    }

    this.ngZone.run(() => {
      gis.initialize({
        client_id: clientId,
        callback: (response: GoogleCredentialCallbackDetail) => {
          this.ngZone.run(() => this.handleCredentialResponse(response));
        },
        auto_select: GOOGLE_SSO_CONFIG.auto_select,
        use_fedcm_for_prompt: true,
      });
    });

    this._isInitialized = true;

    if (isDebugMode()) {
      this.logger.debug('Google Auth: GIS initialized with client ID:', clientId);
    }
  }

  /**
   * Wait for the GIS script to be available (it loads async/defer)
   *
   * @private
   */
  private waitForGisScript(timeoutMs = 5000): Promise<GoogleAccountsId | null> {
    return new Promise((resolve) => {
      const started = Date.now();
      const poll = (): void => {
        const gis = window.google?.accounts?.id;
        if (gis) {
          resolve(gis);
          return;
        }
        if (Date.now() - started > timeoutMs) {
          resolve(null);
          return;
        }
        setTimeout(poll, 100);
      };
      poll();
    });
  }

  /**
   * Handle the GIS credential callback
   *
   * Stores the ID token in memory only and flips credential state. This does
   * NOT establish authentication — only the backend exchange
   * (POST /parent-auth/sso/google) can do that.
   *
   * @private
   */
  private handleCredentialResponse(response: GoogleCredentialCallbackDetail): void {
    if (!response?.credential) {
      this.logger.error('Google Auth: Credential callback returned no credential');
      return;
    }

    this._credential.set(response.credential);
    this._isAuthenticated.set(true);

    if (isDebugMode()) {
      this.logger.debug('Google Auth: Credential acquired (ID token held in memory only)');
    }
  }

  /**
   * Render the official Google sign-in button into a host element
   *
   * The login screen provides a container div; GIS renders its own branded
   * button inside it. Resolves once the button has been rendered (or throws
   * on configuration failure).
   *
   * @param parentElement - Container element for the Google button
   * @param width - The button's width in px (3.19/U24): the login screen passes
   *   its column's width so the GIS button fills the same column as the
   *   Microsoft button. GIS caps it at 400 px and needs at least 200.
   */
  async renderButton(parentElement: HTMLElement, width = 400): Promise<void> {
    await this.initialize();

    const gis = window.google?.accounts?.id;
    if (!gis) {
      throw new Error('Google Identity Services not available');
    }

    this.ngZone.run(() => {
      gis.renderButton(parentElement, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'left',
        width: Math.max(200, Math.min(Math.round(width), 400)),
      });
    });

    if (isDebugMode()) {
      this.logger.debug('Google Auth: Sign-in button rendered');
    }
  }

  /**
   * Acquire a Google ID token via the One Tap / sign-in prompt
   *
   * Alternative to the rendered button: prompts the user with Google's own
   * sign-in UX. The credential arrives via the same callback.
   */
  async prompt(): Promise<void> {
    await this.initialize();

    const gis = window.google?.accounts?.id;
    if (!gis) {
      throw new Error('Google Identity Services not available');
    }

    gis.prompt((notification) => {
      if (isDebugMode()) {
        this.logger.debug('Google Auth: Prompt notification:', notification);
      }
      // 'skipped'/'dismissed' notifications are informational; the credential
      // callback is the only success path.
    });
  }

  /**
   * Sign in (compatibility entry point used by AuthService.loginGoogle)
   *
   * GIS is prompt/button-driven rather than redirect-driven, so this resolves
   * once the SDK is ready and the credential state has been reset; the actual
   * credential arrives asynchronously via the callback wired in initialize().
   */
  async signInWithRedirect(): Promise<void> {
    await this.initialize();
    // Reset any stale credential so a repeated login attempt re-prompts.
    this._credential.set(null);
    this._isAuthenticated.set(false);
  }

  /**
   * Consume the acquired credential (single read)
   *
   * Returns the ID token and clears it from memory — the token is single-use
   * payload for the backend exchange, never a stored credential.
   */
  consumeCredential(): string | null {
    const credential = this._credential();
    this._credential.set(null);
    this._isAuthenticated.set(false);
    return credential;
  }

  /**
   * Get the pending credential without consuming it
   */
  getCurrentCredential(): string | null {
    return this._credential();
  }

  /**
   * Wait for a credential to arrive (used by the login component after
   * rendering the button or prompting)
   */
  waitForCredential(timeoutMs = 120000): Observable<string> {
    return new Observable<string>((subscriber) => {
      const existing = this._credential();
      if (existing) {
        subscriber.next(existing);
        subscriber.complete();
        return;
      }

      const timeoutHandle = setTimeout(() => {
        subscriber.error(new Error('Google sign-in timed out'));
      }, timeoutMs);

      const sub = this.credential$.subscribe((credential) => {
        if (credential) {
          clearTimeout(timeoutHandle);
          subscriber.next(credential);
          subscriber.complete();
        }
      });

      return () => {
        clearTimeout(timeoutHandle);
        sub.unsubscribe();
      };
    });
  }

  /**
   * Sign out of Google
   *
   * GIS has no session to revoke client-side beyond disabling auto-select;
   * the authoritative sign-out is the backend logout (session revocation).
   */
  async signOut(): Promise<void> {
    try {
      const gis = window.google?.accounts?.id;
      gis?.disableAutoSelect();
    } catch (error) {
      if (isDebugMode()) {
        this.logger.warn('Google Auth: disableAutoSelect failed:', error);
      }
    }

    this._credential.set(null);
    this._isAuthenticated.set(false);

    if (isDebugMode()) {
      this.logger.debug('Google Auth: Signed out (client state cleared)');
    }
  }

  /**
   * Get the currently acquired Google credential state
   */
  get isAuthenticated(): boolean {
    return this._isAuthenticated();
  }

  /**
   * Get a decoded view of the current credential's claims (debugging only —
   * the token itself is never logged or stored)
   */
  async getCurrentUser(): Promise<GoogleUser | null> {
    const credential = this._credential();
    if (!credential) {
      return null;
    }
    return firstValueFrom(
      new Observable<GoogleUser | null>((subscriber) => {
        try {
          const payload = JSON.parse(
            atob(credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
          ) as { sub: string; name?: string; email?: string; picture?: string };
          subscriber.next({
            id: payload.sub,
            name: payload.name ?? '',
            email: payload.email ?? '',
            picture: payload.picture,
          });
        } catch {
          subscriber.next(null);
        }
        subscriber.complete();
      }),
    );
  }

  /**
   * Clean up service resources and subscriptions
   */
  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

/**
 * Google user profile (decoded from the ID token payload for display only)
 */
export interface GoogleUser {
  id: string;
  name: string;
  email: string;
  picture?: string;
}